import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { renewalAPI } from '../services/api';
import { format, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { RefreshCw, AlertTriangle, Clock, CheckCircle, XCircle, Phone } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function ExpiryChip({ date }) {
  if (!date) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const d = differenceInDays(new Date(date), new Date());
  if (d < 0) return <span className="badge badge-danger">Expired {Math.abs(d)}d ago</span>;
  if (d === 0) return <span className="badge badge-danger">Today</span>;
  if (d <= 3) return <span className="badge badge-warning">{d}d left</span>;
  if (d <= 7) return <span className="badge badge-info">{d}d left</span>;
  return <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{format(new Date(date), 'dd/MM/yyyy')}</span>;
}

export default function RenewalsPage() {
  const [tab, setTab] = useState('today');
  const [data, setData] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    renewalAPI.getDashboard()
      .then(({ data: d }) => setDashStats(d.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetch = tab === 'expired'
      ? renewalAPI.getExpired()
      : renewalAPI.getUpcoming({ days: tab === 'today' ? 0 : tab === '3days' ? 3 : 7 });

    fetch
      .then(({ data: d }) => setData(d.data))
      .catch(() => toast.error('Failed to load renewals'))
      .finally(() => setLoading(false));
  }, [tab]);

  const statCards = dashStats ? [
    { label: 'Expiring Today', value: dashStats.today, color: '#dc2626', bg: '#fee2e2', icon: AlertTriangle, key: 'today' },
    { label: 'Within 3 Days', value: dashStats.in3days, color: '#d97706', bg: '#fef3c7', icon: Clock, key: '3days' },
    { label: 'Within 7 Days', value: dashStats.in7days, color: '#2563eb', bg: '#dbeafe', icon: RefreshCw, key: '7days' },
    { label: 'Already Expired', value: dashStats.expired, color: '#64748b', bg: '#f1f5f9', icon: XCircle, key: 'expired' },
  ] : [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Renewal Dashboard</h2>
          <p className="text-muted text-small">Track expiring and expired subscriptions</p>
        </div>
      </div>

      {/* Stat cards - clickable */}
      <div className="stats-grid mb-6">
        {statCards.map(c => (
          <div key={c.key} className="stat-card" style={{ cursor: 'pointer', border: tab === c.key ? `2px solid ${c.color}` : '1px solid var(--border)' }}
            onClick={() => setTab(c.key)}>
            <div className="stat-icon" style={{ background: c.bg }}>
              <c.icon size={20} color={c.color} />
            </div>
            <div className="stat-info">
              <p>{c.label}</p>
              <h3>{c.value}</h3>
              <div className="trend" style={{ color: c.color }}>Click to view</div>
            </div>
          </div>
        ))}
      </div>

      {/* Customer list */}
      <div className="card">
        <div className="card-header">
          <h3>
            {tab === 'today' && '⚠️ Expiring Today'}
            {tab === '3days' && '🕐 Expiring Within 3 Days'}
            {tab === '7days' && '📅 Expiring Within 7 Days'}
            {tab === 'expired' && '❌ Expired Subscriptions'}
          </h3>
          <span className="badge badge-info">{data.length} customers</span>
        </div>
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <CheckCircle size={32} color="var(--success)" />
              <h3>No customers here</h3>
              <p>All good in this category!</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Broadband Plan</th>
                  <th>BB Expiry</th>
                  <th>CATV Package</th>
                  <th>CATV Expiry</th>
                  <th>Balance</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map(c => (
                  <tr key={c._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Phone size={10} /> {c.mobile}
                      </div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{c.customerId}</div>
                    </td>
                    <td>
                      <span className={`badge badge-${c.customerType}`} style={{ textTransform: 'capitalize' }}>
                        {c.customerType}
                      </span>
                    </td>
                    <td style={{ fontSize: '.82rem' }}>{c.broadband?.plan?.name || '—'}</td>
                    <td><ExpiryChip date={c.broadband?.expiryDate} /></td>
                    <td style={{ fontSize: '.82rem' }}>{c.catv?.package?.name || '—'}</td>
                    <td><ExpiryChip date={c.catv?.expiryDate} /></td>
                    <td>
                      {c.outstandingBalance > 0
                        ? <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: '.82rem' }}>₹{fmt(c.outstandingBalance)}</span>
                        : <span style={{ color: 'var(--success)', fontSize: '.82rem' }}>Clear</span>}
                    </td>
                    <td>
                      <Link to={`/customers/${c._id}`} className="btn btn-primary btn-sm">Collect</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
